import { useState, useRef, useEffect } from 'react'
import { X, Download, UserCog, Tag, ChevronDown } from 'lucide-react'
import { clsx } from 'clsx'
import type { Owner, AccountStatus } from '../types'
import { ACCOUNT_STATUS_OPTIONS } from '../types'

interface BulkActionBarProps {
  selectedCount: number
  owners: Owner[]
  onChangeOwner: (ownerId: string) => void
  onExportSelected: () => void
  onSetStatus: (status: AccountStatus) => void
  onClearSelection: () => void
}

export function BulkActionBar({
  selectedCount,
  owners,
  onChangeOwner,
  onExportSelected,
  onSetStatus,
  onClearSelection,
}: BulkActionBarProps) {
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false)
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)
  const ownerRef = useRef<HTMLDivElement>(null)
  const statusRef = useRef<HTMLDivElement>(null)

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ownerRef.current && !ownerRef.current.contains(event.target as Node)) {
        setShowOwnerDropdown(false)
      }
      if (statusRef.current && !statusRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (selectedCount === 0) return null

  return (
    <div className="sticky top-0 z-30 flex items-center gap-3 bg-primary/95 backdrop-blur-sm text-white rounded-lg px-4 py-3 shadow-lg">
      {/* Selection count */}
      <div className="flex items-center gap-2 mr-2">
        <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 bg-white/20 rounded-full text-sm font-semibold">
          {selectedCount}
        </span>
        <span className="text-sm font-medium">
          {selectedCount === 1 ? 'account' : 'accounts'} selected
        </span>
      </div>

      <div className="w-px h-6 bg-white/30" />

      {/* Change Owner */}
      <div ref={ownerRef} className="relative">
        <button
          onClick={() => {
            setShowOwnerDropdown(!showOwnerDropdown)
            setShowStatusDropdown(false)
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-md text-sm font-medium transition-colors"
        >
          <UserCog size={14} />
          Change Owner
          <ChevronDown size={12} />
        </button>
        {showOwnerDropdown && (
          <div className="absolute top-full left-0 mt-2 w-56 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto z-50">
            {owners.map((owner) => {
              const name =
                owner.first_name && owner.last_name
                  ? `${owner.first_name} ${owner.last_name}`
                  : owner.email?.split('@')[0] || 'Unknown'
              return (
                <button
                  key={owner.id}
                  onClick={() => {
                    onChangeOwner(owner.id)
                    setShowOwnerDropdown(false)
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
                >
                  {name}
                  {owner.email && (
                    <span className="block text-xs text-[var(--text-muted)]">{owner.email}</span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Set Status */}
      <div ref={statusRef} className="relative">
        <button
          onClick={() => {
            setShowStatusDropdown(!showStatusDropdown)
            setShowOwnerDropdown(false)
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-md text-sm font-medium transition-colors"
        >
          <Tag size={14} />
          Set Status
          <ChevronDown size={12} />
        </button>
        {showStatusDropdown && (
          <div className="absolute top-full left-0 mt-2 w-44 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl py-1 z-50">
            {ACCOUNT_STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onSetStatus(option.value)
                  setShowStatusDropdown(false)
                }}
                className="w-full text-left px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors flex items-center gap-2"
              >
                <StatusDot status={option.value} />
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Export Selected */}
      <button
        onClick={onExportSelected}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-md text-sm font-medium transition-colors"
      >
        <Download size={14} />
        Export Selected
      </button>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Clear Selection */}
      <button
        onClick={onClearSelection}
        className="flex items-center gap-1.5 px-2 py-1.5 hover:bg-white/15 rounded-md text-sm transition-colors"
        title="Clear selection"
      >
        <X size={16} />
      </button>
    </div>
  )
}

// Status dot component used in dropdowns and table
export function StatusDot({ status }: { status: string | null }) {
  const colorMap: Record<string, string> = {
    active: 'bg-green-500',
    engaged: 'bg-blue-500',
    archived: 'bg-slate-400',
  }

  return (
    <span
      className={clsx(
        'inline-block w-2 h-2 rounded-full flex-shrink-0',
        status ? colorMap[status] || 'bg-slate-300' : 'bg-slate-300'
      )}
    />
  )
}

// Status badge component for the table
export function StatusBadge({ status }: { status: string | null }) {
  const displayStatus = status || 'active'

  const colorMap: Record<string, string> = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    engaged: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    archived: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  }

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium capitalize',
        colorMap[displayStatus] || colorMap.active
      )}
    >
      <StatusDot status={displayStatus} />
      {displayStatus}
    </span>
  )
}
